"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  value: "all",
  label: "ALL"
}, {
  value: "security",
  label: "SECURITY"
}, {
  value: "privacy",
  label: "PRIVACY"
}, {
  value: "gaming",
  label: "GAMING"
}, {
  value: "business",
  label: "BUSINESS"
}];
function formatDate(dateString?: string) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function BrewGuidesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = usePosts() as ReturnType<typeof usePosts> & {
    currentPage: number;
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="border-[3px] border-border bg-card p-0 animate-pulse" style={{
        boxShadow: "6px 6px 0 var(--border)"
      }}>
            <div className="aspect-[4/3] bg-muted" />
            <div className="p-6 space-y-3">
              <div className="h-3 w-24 bg-muted" />
              <div className="h-6 w-3/4 bg-muted" />
              <div className="h-4 w-full bg-muted" />
              <div className="h-4 w-2/3 bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border-[3px] border-border bg-card p-8 text-center">
        <p className="font-default text-base text-foreground mb-4">
          Failed to load articles.
        </p>
        <Button onClick={refetch} className="bg-primary text-primary-foreground border-[3px] border-border font-sans uppercase tracking-wide rounded-md" style={{
        boxShadow: "6px 6px 0 var(--border)"
      }}>
          Retry
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border-[3px] border-border bg-card p-12 text-center">
        <p className="font-default text-lg text-foreground">
          No articles published yet. Check back soon.
        </p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "INSIGHTS";
        const excerpt = stripHtml(post.excerpt).slice(0, 140);
        return <article key={post.id} data-index={i} className="group border-[3px] border-border bg-card flex flex-col transition-transform duration-150 ease-out hover:translate-x-1 hover:translate-y-1" style={{
          boxShadow: "6px 6px 0 var(--border)"
        }} onMouseEnter={e => {
          e.currentTarget.style.boxShadow = "2px 2px 0 var(--border)";
        }} onMouseLeave={e => {
          e.currentTarget.style.boxShadow = "6px 6px 0 var(--border)";
        }}>
              {}
              <div className="relative border-b-[3px] border-border overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-muted relative">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.03]" /> : <div className="w-full h-full bg-secondary" />}
                  {}
                  <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: "radial-gradient(circle, var(--border) 1px, transparent 1.5px)",
                backgroundSize: "12px 12px",
                opacity: 0.08
              }} />
                </div>

                {}
                <div className="absolute top-3 left-3">
                  <div className="bg-accent border-[3px] border-border px-3 py-1.5" style={{
                clipPath: "polygon(0% 20%, 8% 0%, 22% 12%, 38% 0%, 52% 14%, 68% 0%, 82% 12%, 100% 20%, 92% 50%, 100% 80%, 82% 70%, 68% 100%, 52% 84%, 38% 100%, 22% 70%, 8% 100%, 0% 80%, 10% 50%)"
              }}>
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent-foreground">
                      {categoryName}
                    </span>
                  </div>
                </div>
              </div>

              {}
              <div className="p-6 flex flex-col flex-1 gap-3">
                <span className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {formatDate(post.date)}
                </span>

                <h3 className="font-sans text-xl md:text-2xl font-bold uppercase leading-[1.0] tracking-tight text-foreground">
                  {post.title}
                </h3>

                {excerpt && <p className="font-default text-sm text-muted-foreground leading-relaxed flex-1">
                    {excerpt}
                    {excerpt.length >= 140 ? "..." : ""}
                  </p>}

                <Link to={post.link || "#"} className="inline-flex items-center gap-1.5 font-sans text-sm font-bold uppercase tracking-wide text-foreground underline underline-offset-4 decoration-2 hover:text-primary transition-colors mt-2">
                  READ
                  <ArrowRight className="size-4" />
                </Link>
              </div>
            </article>;
      })}
      </div>

      {}
      {totalPages > 1 && <div className="mt-16 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="border-[3px] border-border bg-card text-foreground font-sans uppercase tracking-wide rounded-md disabled:opacity-40 hover:bg-card hover:translate-x-0.5 hover:translate-y-0.5 transition-transform" style={{
              boxShadow: "4px 4px 0 var(--border)"
            }}>
                  PREV
                </Button>
              </PaginationItem>

              {Array.from({
            length: Math.min(totalPages, 5)
          }, (_, idx) => {
            const pageNum = idx + 1;
            const isActive = pageNum === currentPage;
            return <PaginationItem key={pageNum}>
                    <Button variant="ghost" onClick={() => setPage(pageNum)} className={`size-10 p-0 border-[3px] border-border font-sans font-bold rounded-md transition-transform hover:translate-x-0.5 hover:translate-y-0.5 ${isActive ? "bg-accent text-accent-foreground" : "bg-card text-foreground hover:bg-card"}`} style={{
                boxShadow: "4px 4px 0 var(--border)"
              }}>
                      {pageNum}
                    </Button>
                  </PaginationItem>;
          })}

              {totalPages > 5 && <PaginationItem>
                  <PaginationEllipsis className="text-foreground" />
                </PaginationItem>}

              <PaginationItem>
                <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="border-[3px] border-border bg-card text-foreground font-sans uppercase tracking-wide rounded-md disabled:opacity-40 hover:bg-card hover:translate-x-0.5 hover:translate-y-0.5 transition-transform" style={{
              boxShadow: "4px 4px 0 var(--border)"
            }}>
                  NEXT
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function Relatedvpnarticles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1537" id="relatedvpnarticles" className="bg-background text-foreground border-y-[4px] border-border py-24 md:py-32 px-5 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1.5px)",
      backgroundSize: "14px 14px",
      opacity: 0.04
    }} />

      <div className="max-w-[1440px] mx-auto relative">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20">
          {}
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-muted-foreground">
                PANEL 01
              </span>
              <span className="size-1.5 bg-foreground" />
              <span className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-muted-foreground">
                VPN INSIGHTS
              </span>
            </div>

            <h2 className="font-sans text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold uppercase leading-[0.92] tracking-tighter text-foreground">
              SECURE{" "}
              <span className="inline-block bg-accent text-accent-foreground border-[3px] border-border px-3 md:px-5 py-0 md:py-1" style={{
              transform: "rotate(-1.5deg)",
              boxShadow: "6px 6px 0 var(--border)"
            }}>
                DIGITALLY
              </span>
            </h2>

            <p className="font-default text-base md:text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">
              Expert guides, privacy tips, and cybersecurity news from the VPNGENIX team.
            </p>
          </div>

          {}
          <div className="lg:col-span-3 flex lg:items-end lg:justify-end">
            <ToggleGroup type="single" defaultValue="all" className="flex flex-wrap gap-2 rounded-md">
              {categories.map((cat, i) => <ToggleGroupItem key={cat.value} data-index={i} value={cat.value} className="border-[2px] border-border bg-card text-foreground font-mono text-xs font-semibold uppercase tracking-[0.15em] px-3 py-2 rounded-md data-[state=on]:bg-accent data-[state=on]:text-accent-foreground hover:bg-muted transition-colors">
                  {cat.label}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        order: "desc",
        orderby: "date"
      }}>
          <BrewGuidesConsumer />
        </PostsProvider>
      </div>
    </section>;
}